import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ClipboardCheck, Loader2, AlertTriangle, CheckCircle2, AlertCircle, XCircle, RefreshCw } from 'lucide-react';

const ICON = { READY: CheckCircle2, WARNING: AlertCircle, BLOCKED: XCircle };
const COLOR = { READY: 'text-emerald-600', WARNING: 'text-amber-600', BLOCKED: 'text-destructive' };
const BANNER = {
  READY: 'bg-emerald-500/12 text-emerald-700 border-emerald-500/30',
  WARNING: 'bg-amber-500/12 text-amber-700 border-amber-500/30',
  BLOCKED: 'bg-destructive/10 text-destructive border-destructive/30',
};

export default function AdminProductionReadiness() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => { setBusy(true); api.adminProductionReadiness().then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }).finally(() => setBusy(false)); };
  useEffect(() => { document.title = 'Production readiness | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Admin access required</h1></div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-accent" /> Production readiness</h1>
          <Button onClick={load} disabled={busy} variant="outline" className="rounded-xl" data-testid="readiness-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />Re-run</Button>
        </div>
        {!data ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <>
            <div className={`rounded-2xl border p-4 mb-5 flex items-center justify-between ${BANNER[data.overall]}`} data-testid="readiness-banner">
              <div>
                <p className="text-xs uppercase tracking-wide opacity-80">Overall status · env: {data.app_env}</p>
                <p className="font-display text-xl font-semibold">{data.overall}</p>
              </div>
              <div className="flex gap-3 text-sm">
                <span className="text-emerald-600 font-semibold">{data.counts.READY} ready</span>
                <span className="text-amber-600 font-semibold">{data.counts.WARNING} warning</span>
                <span className="text-destructive font-semibold">{data.counts.BLOCKED} blocked</span>
              </div>
            </div>
            <div className="grid gap-2" data-testid="readiness-items">
              {data.items.map((it) => {
                const Ic = ICON[it.status] || AlertCircle;
                return (
                  <Card key={it.key} className="p-3 rounded-xl flex items-start gap-3" data-testid={`readiness-${it.key}`}>
                    <Ic className={`h-5 w-5 mt-0.5 shrink-0 ${COLOR[it.status]}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm">{it.label}</p>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${COLOR[it.status]}`}>{it.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{it.reason}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
